<script setup>
import { computed, reactive, ref } from 'vue';
import Modal from '../Modal.vue';
import XlAvatar from '../xl/XlAvatar.vue';
import PgeButton from '../../pge-components/pge-button.vue';
import FormInput from '../../pge-components/pge-input-text.vue';
import IconComponent from '../icons/index.vue';
import { accessSummaryLine } from '../../composables/useResourceAccess';
import { uploadResourcesFor } from '../../api/memberResources';
import { plural } from '../../helpers/plural';

const props = defineProps({
  member: { type: Object, required: true },
  files: { type: Array, required: true },
  members: { type: Array, default: () => [] },
  // Set where the caller has a Save of its own: the choice is handed back
  // instead of written, and that Save is what puts the file on the server.
  deferred: { type: Boolean, default: false },
});

const emit = defineEmits(['cancel', 'uploaded', 'staged']);

// Exclusive, because the API cannot hold both: save() clears member_ids and
// uploaded_for_user_ids whenever all_participants is set.
const ACCESS_MODES = ['member', 'everyone', 'specific'];
const accessMode = ref('member');

const form = reactive({
  member_ids: [props.member.id],
});

const search = ref('');
const uploading = ref(false);

const isEveryone = computed(() => accessMode.value === 'everyone');
const showPicker = computed(() => accessMode.value === 'specific');

const setAccessMode = (mode) => {
  if (! ACCESS_MODES.includes(mode) || accessMode.value === mode) return;
  accessMode.value = mode;
  // Leaving the picker drops everyone chosen there; the member the upload is
  // for is the one constant across all three modes.
  if (mode !== 'specific') form.member_ids = [props.member.id];
};

const selectedMembers = computed(() =>
  form.member_ids.map(id => props.members.find(m => m.id === id) || { id, label: '' })
);

const filteredMembers = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return props.members;
  return props.members.filter(m =>
    (m.label || '').toLowerCase().includes(q) ||
    (m.email || '').toLowerCase().includes(q)
  );
});

const noSearchResults = computed(() => search.value.trim() && filteredMembers.value.length === 0);

const isSelected = (id) => form.member_ids.includes(id);

const toggleMember = (id) => {
  // The member this upload is for cannot be removed — the whole action is
  // scoped to them, and the Members page is the only way back to it.
  if (id === props.member.id) return;

  const index = form.member_ids.indexOf(id);
  if (index === -1) {
    form.member_ids.push(id);
  } else {
    form.member_ids.splice(index, 1);
  }
};

const accessPayload = computed(() => (isEveryone.value
  ? {
    all_participants: true,
    member_ids: [],
    uploaded_for_user_ids: [],
  }
  : {
    all_participants: false,
    member_ids: [...form.member_ids],
    // Everyone named is a recipient, not just the member the action started
    // from, or the rest get access without the file reaching their My
    // Resources or their count.
    uploaded_for_user_ids: [...form.member_ids],
  }));

const summary = computed(() => accessSummaryLine(accessPayload.value, props.members));

const recipientsLabel = computed(() => {
  if (isEveryone.value) return 'everyone';
  return form.member_ids.length > 1 ? plural(form.member_ids.length, 'policyholder') : props.member.label;
});

const buttonLabel = computed(() => `${props.deferred ? 'Add' : 'Upload'} for ${recipientsLabel.value}`);

const submit = async () => {
  if (uploading.value || !props.files.length) return;

  if (props.deferred) {
    emit('staged', { files: [...props.files], access: accessPayload.value });
    return;
  }

  uploading.value = true;

  try {
    const mediaIds = await uploadResourcesFor({ files: props.files, access: accessPayload.value });

    window.loadNotification?.({
      message: mediaIds.length === 1
        ? `Uploaded for ${recipientsLabel.value}`
        : `${mediaIds.length} resources uploaded for ${recipientsLabel.value}`,
    });

    emit('uploaded', mediaIds);
  } catch (error) {
    console.error(error);
    window.loadNotification?.({ message: 'Upload failed', error: true });
  } finally {
    uploading.value = false;
  }
};
</script>

<template>
  <modal
    title="Upload resources"
    max-width="520px"
    content-with-border
    :on-close="() => emit('cancel')"
    @close="emit('cancel')"
  >
    <template #content>
      <div class="upload-for">
        <div class="upload-for__banner">
          <icon-component icon="Info" size="18" />
          <span>Uploading for <strong>{{ member.label }}</strong> · {{ member.email }}</span>
        </div>

        <p class="upload-for__label">Files</p>
        <ul class="upload-for__files">
          <li v-for="file in files" :key="file.name" class="truncate">{{ file.name }}</li>
        </ul>

        <p class="upload-for__roles-label">WHO CAN ACCESS THIS RESOURCE?</p>

        <button
          type="button"
          class="upload-for__option"
          :class="{ 'is-selected': accessMode === 'member' }"
          @click="setAccessMode('member')"
        >
          <span class="upload-for__radio" />
          <span>
            <span class="upload-for__option-label">This policyholder only</span>
            <span class="upload-for__option-sub">Visible only in their My Resources tab</span>
          </span>
        </button>

        <button
          type="button"
          class="upload-for__option"
          :class="{ 'is-selected': accessMode === 'everyone' }"
          @click="setAccessMode('everyone')"
        >
          <span class="upload-for__radio" />
          <span>
            <span class="upload-for__option-label">Everyone</span>
            <span class="upload-for__option-sub">Visible to all hub participants</span>
          </span>
        </button>

        <button
          type="button"
          class="upload-for__option"
          :class="{ 'is-selected': accessMode === 'specific' }"
          @click="setAccessMode('specific')"
        >
          <span class="upload-for__radio" />
          <span>
            <span class="upload-for__option-label">Specific policyholders</span>
            <span class="upload-for__option-sub">Choose additional policyholders to share with</span>
          </span>
        </button>

        <div v-if="showPicker && selectedMembers.length" class="upload-for__chips">
          <span v-for="m in selectedMembers" :key="m.id" class="upload-for__chip">
            <XlAvatar :name="m.label" :src="m.img || ''" :color="m.color || ''" :size="24" />
            <span class="truncate">{{ m.label }}</span>
            <button v-if="m.id !== member.id" type="button" @click="toggleMember(m.id)">
              <icon-component icon="Close" size="14" />
            </button>
          </span>
        </div>

        <form-input
          v-if="showPicker"
          :model="search"
          v-model="search"
          border-circle
          has-search-icon
          placeholder="Search by name or email..."
        />

        <p v-if="showPicker && noSearchResults" class="upload-for__empty">
          No policyholders found. Only registered hub participants can be added.
        </p>

        <div v-else-if="showPicker" class="upload-for__list">
          <div v-for="m in filteredMembers" :key="m.id" class="upload-for__list-item">
            <XlAvatar :name="m.label" :src="m.img || ''" :color="m.color || ''" :size="24" />
            <span class="upload-for__list-name truncate">{{ m.label }}</span>
            <button
              type="button"
              class="upload-for__list-action"
              :class="isSelected(m.id) ? 'is-remove' : 'is-add'"
              :disabled="m.id === member.id"
              @click="toggleMember(m.id)"
            >
              {{ m.id === member.id ? 'Recipient' : (isSelected(m.id) ? 'Remove' : 'Add') }}
            </button>
          </div>
        </div>

        <div class="upload-for__summary">
          <icon-component icon="Info" size="18" />
          <span>{{ summary }}</span>
        </div>
      </div>
    </template>

    <template #actions>
      <pge-button template="outlined" label="Cancel" :disabled="uploading" @click="emit('cancel')" />
      <pge-button :label="buttonLabel" :disabled="uploading" @click="submit" />
    </template>
  </modal>
</template>

<style lang="scss" scoped>
.upload-for {
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-size: 14px;
  color: #1D1B20;

  &__banner {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px;
    border-radius: 8px;
    background: #DCEBFB;
    color: #1B4F8A;
    font-size: 13px;
  }

  &__label {
    margin: 0;
    font-weight: 600;
  }

  &__roles-label {
    margin: 0;
    font-size: 11px;
    letter-spacing: 0.5px;
    color: #79747E;
  }

  &__option {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 9px 11px;
    border: 1.5px solid transparent;
    border-radius: 9px;
    background: transparent;
    text-align: left;
    cursor: pointer;

    &:hover { background: #F8F7FF; border-color: #E0DCFD; }

    &.is-selected {
      background: #F0EEFF;
      border-color: #A89DF0;

      .upload-for__radio {
        border-color: #5E2F78;
        background: #5E2F78;
        box-shadow: inset 0 0 0 3px #fff;
      }
    }
  }

  &__radio {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    border: 2px solid #CAC4D0;
    border-radius: 50%;
  }

  &__option-label {
    display: block;
    font-size: 14px;
    font-weight: 500;
  }

  &__option-sub {
    display: block;
    font-size: 11px;
    color: #79747E;
  }

  &__files {
    margin: 0;
    padding-left: 20px;
    max-height: 120px;
    overflow-y: auto;

    li { line-height: 22px; }
  }

  &__role {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 12px;
    border: 1px solid #CAC4D0;
    border-radius: 8px;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px 4px 4px;
    border: 1px solid #CAC4D0;
    border-radius: 999px;
    max-width: 100%;

    button {
      display: flex;
      border: 0;
      background: transparent;
      cursor: pointer;
      padding: 0;
    }
  }

  &__empty {
    margin: 0;
    color: #79747E;
    font-size: 13px;
  }

  &__list {
    display: flex;
    flex-direction: column;
    max-height: 200px;
    overflow-y: auto;
  }

  &__list-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 0;
  }

  &__list-name { flex: 1; }

  &__list-action {
    border: 0;
    background: transparent;
    cursor: pointer;
    font-weight: 600;

    &.is-add { color: #5E2F78; }
    &.is-remove { color: #B3261E; }

    &:disabled {
      color: #79747E;
      cursor: default;
    }
  }

  &__summary {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px;
    border-radius: 8px;
    background: #EDE7F6;
    color: #5E2F78;
    font-size: 13px;
  }
}
</style>
